import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { logementsOverviewService } from '@/services/logementsOverviewService';
import type { LogementsOverviewResponse } from '@/services/logementsOverviewService';

function formatNumber(value: number | null | undefined, digits = 1): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A';
  return value.toFixed(digits);
}

function formatPercent01(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A';
  return `${Math.round(value * 100)}%`;
}

export function ProjectAnalysesLogementPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const id = projectId ? Number(projectId) : NaN;
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, isRoot, isAdmin } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getUserInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const { data, isLoading, error } = useQuery<LogementsOverviewResponse>({
    queryKey: ['logements-overview', id],
    queryFn: () => logementsOverviewService.getOverview({ projectId: id }),
    enabled: Number.isFinite(id),
    retry: 1,
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const outOfComfort = useMemo(
    () => rows.filter((r) => typeof r.comfortPct === 'number' && r.comfortPct < 0.7),
    [rows]
  );

  if (!isRoot() && !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500">Accès refusé. Vous devez être administrateur.</p>
        </div>
      </div>
    );
  }

  if (!Number.isFinite(id)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Identifiant projet invalide</p>
          <button onClick={() => navigate('/admin/projects')} className="mt-4 text-primary hover:text-primary-600">
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Erreur lors du chargement des données logements</p>
          <button onClick={() => navigate('/admin/projects')} className="mt-4 text-primary hover:text-primary-600">
            Retour aux projets
          </button>
        </div>
      </div>
    );
  }

  const handleRowClick = (_areaId: number) => {
    // Placeholder: navigate to a logement detail route if/when it exists.
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button onClick={() => setSidebarOpen(true)} className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white">
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Projets / Analyses / Logement
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                {user?.fullName ? getUserInitials(user.fullName) : 'AD'}
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Logements</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Période: {data.range.from} → {data.range.to} • Confort: {data.comfort.min}°C–{data.comfort.max}°C
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <section className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <div className="p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Synthèse parc logements</h2>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">Logements</div>
                    <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{data.summary.logementsCount}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">Temp. moyenne globale</div>
                    <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{formatNumber(data.summary.avgTemperatureGlobal)}°C</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">% hors confort</div>
                    <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{formatPercent01(data.summary.outOfComfortPct)}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">Écart max moyennes</div>
                    <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{formatNumber(data.summary.maxAvgGapBetweenLogements)}°C</div>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <div className="p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Logements hors confort</h2>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  {outOfComfort.length} logement(s) avec <strong>moins de 70%</strong> du temps en confort.
                </p>
              </div>
            </section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <section className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <div className="p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Logements les plus chauds</h2>
                <div className="mt-3 space-y-2">
                  {data.top.hot.length === 0 ? (
                    <div className="text-sm text-slate-500 dark:text-slate-400">Aucune donnée</div>
                  ) : (
                    data.top.hot.map((i) => (
                      <div key={i.areaId} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700 dark:text-slate-200 truncate">{i.name}</span>
                        <span className="text-slate-500 dark:text-slate-400">{formatNumber(i.avgTemp)}°C</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            <section className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <div className="p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Logements les plus froids</h2>
                <div className="mt-3 space-y-2">
                  {data.top.cold.length === 0 ? (
                    <div className="text-sm text-slate-500 dark:text-slate-400">Aucune donnée</div>
                  ) : (
                    data.top.cold.map((i) => (
                      <div key={i.areaId} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700 dark:text-slate-200 truncate">{i.name}</span>
                        <span className="text-slate-500 dark:text-slate-400">{formatNumber(i.avgTemp)}°C</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            <section className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <div className="p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Logements les plus instables</h2>
                <div className="mt-3 space-y-2">
                  {data.top.unstable.length === 0 ? (
                    <div className="text-sm text-slate-500 dark:text-slate-400">Aucune donnée</div>
                  ) : (
                    data.top.unstable.map((i) => (
                      <div key={i.areaId} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700 dark:text-slate-200 truncate">{i.name}</span>
                        <span className="text-slate-500 dark:text-slate-400">{formatNumber(i.amplitude)}°C</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>
          </div>

          <section className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Liste des logements</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-[#1E293B]">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      <th className="px-4 py-3">Logement</th>
                      <th className="px-4 py-3">Temp. moyenne</th>
                      <th className="px-4 py-3">Min</th>
                      <th className="px-4 py-3">Max</th>
                      <th className="px-4 py-3">Amplitude</th>
                      <th className="px-4 py-3">% temps confort</th>
                      <th className="px-4 py-3">Score confort</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-[#1E293B]">
                    {rows.map((r) => (
                      <tr
                        key={r.areaId}
                        onClick={() => handleRowClick(r.areaId)}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-[#0B1116]"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">{r.name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{formatNumber(r.avgTemp)}°C</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{formatNumber(r.minTemp)}°C</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{formatNumber(r.maxTemp)}°C</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{formatNumber(r.amplitude)}°C</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{formatPercent01(r.comfortPct)}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {typeof r.comfortScore === 'number' ? r.comfortScore : 'N/A'}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400" colSpan={7}>
                          Aucun logement.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

